/**
 * CalendarToolbar — la barra de dalt del calendari, en una línia i plegable (F7 · en Pere 29-SET).
 *
 * Oberta: una fila amb el rang de dates (text petit), l'«Actualitzant…», la vista (Pestanyes /
 * Totes) a l'esquerra i, a la dreta, el que passi la pàgina (Llits per revisar, ‹ Avui ›, calendari,
 * roda dentada) i el botó de plegar (⌃). Les pestanyes de propietat, si n'hi ha, en una fila baixa
 * a sota (`below`): amb 5 propietats de noms llargs no caben a la mateixa fila sense amagar-ne.
 *
 * Plegada: només una tira de 20 px amb el rang, l'«Actualitzant…» i el botó per tornar-la a obrir
 * (⌄). L'estat es desa per dispositiu (`calendarV2_settings.headerCollapsed`).
 */

import type { ReactNode } from 'react';
import { ChevronDown, ChevronUp, Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';

export const TOOLBAR_EXPAND_LABEL = 'Mostrar la barra del calendari';
export const TOOLBAR_COLLAPSE_LABEL = 'Amagar la barra del calendari';

export interface CalendarToolbarProps {
  /** «29 de set. - 12 d’oct.» */
  rangeLabel: string;
  /** Dades d'abans mentre n'arriben de noves (P7): «Actualitzant…» amb `role="status"`. */
  updating: boolean;
  collapsed: boolean;
  onCollapsedChange: (collapsed: boolean) => void;
  /** A l'esquerra de la fila, després del rang: la vista (Pestanyes / Totes). */
  leading?: ReactNode;
  /** A la dreta de la fila, abans del botó de plegar. */
  trailing?: ReactNode;
  /** Fila baixa a sota (les pestanyes de propietat). No es pinta si és buida o plegada. */
  below?: ReactNode;
}

/** P7: blau-700 sobre blanc, 6,7:1; sempre a la vista, també amb la barra plegada. */
function UpdatingStatus({ className }: { className?: string }) {
  return (
    <span role="status" className={cn('flex items-center gap-1 font-medium text-blue-700', className)}>
      <Loader2 className="h-3 w-3 animate-spin" aria-hidden="true" />
      Actualitzant…
    </span>
  );
}

export function CalendarToolbar({
  rangeLabel,
  updating,
  collapsed,
  onCollapsedChange,
  leading,
  trailing,
  below,
}: CalendarToolbarProps) {
  if (collapsed) {
    return (
      <div
        data-testid="calendar-toolbar-collapsed"
        className="flex h-[19px] items-center gap-2 px-4 text-[11px] leading-none text-gray-500"
      >
        <span className="whitespace-nowrap">{rangeLabel}</span>
        {updating && <UpdatingStatus />}
        <button
          type="button"
          onClick={() => onCollapsedChange(false)}
          aria-label={TOOLBAR_EXPAND_LABEL}
          title={TOOLBAR_EXPAND_LABEL}
          aria-expanded={false}
          className="ml-auto flex h-[19px] w-8 items-center justify-center rounded hover:bg-gray-100 active:bg-gray-200 touch-manipulation"
        >
          <ChevronDown className="h-4 w-4 text-gray-600" aria-hidden="true" />
        </button>
      </div>
    );
  }

  return (
    <>
      <div
        data-testid="calendar-toolbar"
        className="flex flex-wrap items-center gap-x-3 gap-y-1 px-4 py-1"
      >
        <div className="flex min-w-0 flex-1 basis-64 items-center gap-2">
          <span className="whitespace-nowrap text-xs text-gray-500">{rangeLabel}</span>
          {updating && <UpdatingStatus className="text-xs" />}
          {leading}
        </div>
        <div className="ml-auto flex flex-wrap items-center justify-end gap-1">
          {trailing}
          <button
            type="button"
            onClick={() => onCollapsedChange(true)}
            aria-label={TOOLBAR_COLLAPSE_LABEL}
            title={TOOLBAR_COLLAPSE_LABEL}
            aria-expanded
            className="p-1.5 rounded-lg hover:bg-gray-100 active:bg-gray-200 transition-colors touch-manipulation"
          >
            <ChevronUp className="w-5 h-5 text-gray-600" aria-hidden="true" />
          </button>
        </div>
      </div>
      {below}
    </>
  );
}

export default CalendarToolbar;
